import FetchWorkflowRunKinds from '@Application/WorkflowRuns/WorkflowRunKinds/Queries/FetchWorkflowRunKinds';
import fetchWorkflowRunKinds from '@Application/WorkflowRuns/WorkflowRunKinds/fetchWorkflowRunKinds';
import FetchWorkflowRunStatusFilters from '@Application/WorkflowRuns/WorkflowRunStatusFilters/Queries/FetchWorkflowRunStatusFilters';
import fetchWorkflowRunStatusFilters from '@Application/WorkflowRuns/WorkflowRunStatusFilters/fetchWorkflowRunStatusFilters';
import { PermissionEnum } from '@Application/Security/Roles';
import FilterPanelSidebar from '@Guidelines/Component/FilterPanel/FilterPanelSidebar';
import FilterPanelTrigger from '@Guidelines/Component/FilterPanel/FilterPanelTrigger';
import useFilterPanel from '@Guidelines/Component/FilterPanel/Hooks/useFilterPanel';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import AssignedUserFilter from '@UI/Pages/WorkflowRuns/Filters/AssignedUserFilter';
import BeneficiaryFilter from '@UI/Pages/WorkflowRuns/Filters/BeneficiaryFilter';
import CustomRangeFilter from '@UI/Pages/WorkflowRuns/Filters/CustomRangeFilter';
import KindFilter from '@UI/Pages/WorkflowRuns/Filters/KindFilter';
import PeriodFilter from '@UI/Pages/WorkflowRuns/Filters/PeriodFilter';
import WorkflowRunFilterView from '@UI/Pages/WorkflowRuns/Filters/WorkflowRunFilterView';
import { setWorkflowRunFilters } from '@UI/Pages/WorkflowRuns/Filters/Signals/workflowRunFiltersSignal';
import StatusFilter from '@UI/Pages/WorkflowRuns/Filters/StatusFilter';
import useEffectiveWorkflowRunFilters from '@UI/Pages/WorkflowRuns/Hooks/useEffectiveWorkflowRunFilters';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import type BeneficiaryDirectoryEntryView from '@UI/Store/Beneficiaries/Views/BeneficiaryDirectoryEntryView';
import type FacilityUserView from '@UI/Store/FacilityUsers/Views/FacilityUserView';
import * as React from 'react';
import { Grid, GridItem } from '@verclam/grid-system';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Message } from '@verclam/ui-kit';
import ConditionalViewPort from '@Guidelines/Component/Loader/ConditionalViewPort/ConditionalViewPort';
import { css } from '@emotion/react';
import { theme } from '@Guidelines/Theme/theme';

const stickyFilter = css`
	position: sticky;
	top: 0;
	background-color: ${theme.colors.canvas};
	z-index: 1;
`;

const REFERENT_PERMISSIONS = [PermissionEnum.WORKFLOW_RUN_FACILITY_READ, PermissionEnum.USER_READ] as const;

type Props = {
	readonly beneficiaries: readonly BeneficiaryDirectoryEntryView[];
	readonly users: readonly FacilityUserView[];
};

/**
 * Each field writes into `workflowRunFiltersSignal` itself and reads the filters in effect through
 * `useEffectiveWorkflowRunFilters`, never as a prop (`guides/lists-and-filters.md`). Status, kind
 * and period are server-decided vocabularies — the two enum catalogues load here, on the same
 * uncached, `stopOn: (value) => value !== null` pattern as `AuditTrail/Filters/index.tsx`, so a first
 * load failure never blocks the list, which keeps working under the default `all` status.
 */
const Filters: React.FC<Props> = ({ beneficiaries, users }) => {
	const filters = useEffectiveWorkflowRunFilters();
	const dispatch = useDispatch();
	const { isCompact, isOpen, panelId, open, close } = useFilterPanel();

	const { data: statusFilters, error: statusFiltersError } = useStoreQuery({
		selector: (state) => state.workflowRunsStore.statusFilters,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchWorkflowRunStatusFilters(new FetchWorkflowRunStatusFilters())),
	});

	const { data: kinds, error: kindsError } = useStoreQuery({
		selector: (state) => state.workflowRunsStore.kinds,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchWorkflowRunKinds(new FetchWorkflowRunKinds())),
	});

	const handleReset = React.useCallback(() => {
		setWorkflowRunFilters(WorkflowRunFilterView.createDefault(filters.facilityId, filters.userId));
	}, [filters.facilityId, filters.userId]);

	const isCustomPeriod = filters.period === 'custom';

	return (
		<React.Fragment>
			<ConditionalLoader data={statusFiltersError !== null || kindsError !== null}>
				<Message severity="error" text="Impossible de charger les filtres." />
			</ConditionalLoader>
			<Grid
				columns={10}
				columnGap={1}
				rowGap={1}
				alignItems="end"
				css={isCompact ? stickyFilter : undefined}
			>
				<GridItem columnSize={{ xl: 2, lg: 2, md: 7 }}>
					<StatusFilter options={statusFilters ?? []} />
				</GridItem>
				<ConditionalViewPort
					whenCompact={
						<GridItem columnSize={{ md: 3 }} alignSelf={'end'} css={{ paddingBottom: '0.5rem' }}>
							<FilterPanelTrigger
								panelId={panelId}
								isOpen={isOpen}
								activeCount={filters.activeFilterCount}
								onClick={open}
							/>
						</GridItem>
					}
				>
					<GridItem columnSize={{ xl: 2, lg: 2, md: 5 }}>
						<KindFilter options={kinds} />
					</GridItem>
					<GridItem columnSize={{ xl: 2, lg: 2, md: 5 }}>
						<PeriodFilter />
					</GridItem>
					<ConditionalLoader data={isCustomPeriod}>
						<GridItem columnSize={{ xl: 2, lg: 2, md: 5 }}>
							<CustomRangeFilter />
						</GridItem>
					</ConditionalLoader>
					<GridItem columnSize={{ xl: 2, lg: 2, md: 5 }}>
						<BeneficiaryFilter options={beneficiaries} />
					</GridItem>
					<WithPermission permission={REFERENT_PERMISSIONS} mode="all">
						<GridItem columnSize={{ xl: 2, lg: 2, md: 5 }}>
							<AssignedUserFilter options={users} />
						</GridItem>
					</WithPermission>
				</ConditionalViewPort>
			</Grid>
			<FilterPanelSidebar
				panelId={panelId}
				visible={isOpen}
				activeCount={filters.activeFilterCount}
				onHide={close}
				onReset={handleReset}
			>
				<Grid columns={1} rowGap={1}>
					<GridItem>
						<KindFilter options={kinds} />
					</GridItem>
					<GridItem>
						<PeriodFilter />
					</GridItem>
					<ConditionalLoader data={isCustomPeriod}>
						<GridItem>
							<CustomRangeFilter />
						</GridItem>
					</ConditionalLoader>
					<GridItem>
						<BeneficiaryFilter options={beneficiaries} />
					</GridItem>
					<WithPermission permission={REFERENT_PERMISSIONS} mode="all">
						<GridItem>
							<AssignedUserFilter options={users} />
						</GridItem>
					</WithPermission>
				</Grid>
			</FilterPanelSidebar>
		</React.Fragment>
	);
};

export default Filters;
